import { Paragraph } from '@quickmd-llc/design-system'
import type { PatientAlertStaffApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { getAuthorName } from 'utils/common'
import { formatAlertDateTime } from '../formatAlertDateTime'

/**
 * Alerts are the one place `Author.UserType` reaches the wire unconverted: the collection handler
 * uses the `AuthorApiHelper.ConvertToAuthorWithTypeOutContract(Author)` overload, which assigns the
 * raw value, rather than `UserTypeApiHelper.ConvertToContract`, which every other endpoint runs and
 * which would have turned `doctor` into `provider`. So these are the data-model values.
 *
 * `doctor` renders as "Provider" because the author is often a nurse practitioner or physician
 * assistant, not a physician. The patient chart still shows "Doctor" for the same data
 * (PatientChart.tsx) — aligning it is deliberately out of QMD-4717's scope.
 */
const AUTHOR_ROLE_LABELS: Record<string, string> = Object.assign(Object.create(null), {
  doctor: 'Provider',
  admin: 'Admin',
  patient: 'Patient',
})

type Props = {
  alert: PatientAlertStaffApiOutContract
}

export const VisitAlertCard = ({ alert }: Props) => {
  // `getAuthorName` only substitutes 'System generated' for a null Author; an Author whose
  // name fields are all null trims to an empty string, so fall back here too.
  const authorName = getAuthorName(alert.Author, false).trim() || 'System generated'
  const role = AUTHOR_ROLE_LABELS[alert.Author?.UserType?.toLowerCase() ?? '']

  return (
    <div className='flex flex-col gap-sp-0.5 p-sp-2'>
      <div className='flex items-center gap-sp-1'>
        <Paragraph size='xs' className='text-text-dark'>
          {authorName}
        </Paragraph>
        {role ?
          <Paragraph size='xs' className='text-text-light'>
            {role}
          </Paragraph>
        : null}
      </div>
      <Paragraph size='xs' className='text-text-light'>
        {formatAlertDateTime(alert.UpdateDateTime)}
      </Paragraph>
      <Paragraph size='sm' className='whitespace-pre-wrap break-words text-text-dark'>
        {alert.Message}
      </Paragraph>
    </div>
  )
}
